import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Card, Empty, Spin } from 'antd';
import {
  Area,
  XAxis,
  YAxis,
  Tooltip,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';

import { DashboardService } from '@/services';
import ButtonComponent from '@/components/Button';
import { parsePIChartData } from '@/helpers/dashboard';
import './index.scss';

function CustomTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    return (
      <div className="chart__custom-tooltip">
        <p className="label">{payload[0].value.toLocaleString()}</p>
      </div>
    );
  }

  return null;
}

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
};

function ChartSection() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [disableFilter, setDisableFilter] = useState(true);
  const [datePeriod, setDatePeriod] = useState(7);
  const [dataChart, setDataChart] = useState([]);

  const fetchSmsInsight = async () => {
    setLoading(true);
    const { success, data } = await DashboardService.getSmsInsight({
      datePeriod,
    });

    if (success) {
      const groupBy = datePeriod === 30 ? 'week' : 'day';
      const dataChartParsed = parsePIChartData({
        data,
        groupBy,
      });
      setDataChart(dataChartParsed);
      setDisableFilter(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSmsInsight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datePeriod]);

  const DataFormater = (number) => {
    if (number > 1000000000) {
      return `${(number / 1000000000).toString()}B`;
    }
    if (number > 1000000) {
      return `${(number / 1000000).toString()}M`;
    }
    if (number > 1000) {
      return `${(number / 1000).toString()}K`;
    }
    return number.toString();
  };

  const renderChart = () => {
    if (dataChart?.length === 0) {
      return <Empty />;
    }

    return (
      <AreaChart data={dataChart}>
        <defs>
          <linearGradient id="sms" x1="0" y1="0" x2="0" y2="1">
            <stop stopColor="#9254DE" stopOpacity={1} />
            <stop offset="60%" stopColor="#9254DE" stopOpacity={0.2} />
          </linearGradient>
        </defs>

        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis dataKey="name" axisLine={false} tickLine={false} />
        <YAxis tickFormatter={DataFormater} axisLine={false} tickLine={false} />
        <Tooltip content={<CustomTooltip />} cursor={false} />
        <Area
          dataKey="sms"
          stroke="#3C0CFF"
          fill="url(#sms)"
          activeDot={{ stroke: '#3C0CFF', r: 7 }}
        />
      </AreaChart>
    );
  };

  return (
    <Card
      title={t('dashboard.sms_used_insights_section.title')}
      bordered={false}
      className="dashboard-chart-container"
      extra={
        <div className="filter-container">
          <ButtonComponent
            disabled={disableFilter}
            type={datePeriod === 7 ? 'primary' : 'link'}
            onClick={() => setDatePeriod(7)}
          >
            {t('dashboard.sms_used_insights_section.filter.seven_days')}
          </ButtonComponent>
          <ButtonComponent
            disabled={disableFilter}
            type={datePeriod === 14 ? 'primary' : 'link'}
            onClick={() => setDatePeriod(14)}
          >
            {t('dashboard.sms_used_insights_section.filter.fourteen_days')}
          </ButtonComponent>
          <ButtonComponent
            disabled={disableFilter}
            type={datePeriod === 30 ? 'primary' : 'link'}
            onClick={() => setDatePeriod(30)}
          >
            {t('dashboard.sms_used_insights_section.filter.one_month')}
          </ButtonComponent>
        </div>
      }
    >
      <ResponsiveContainer
        width="100%"
        height={210}
        className={`${loading ? ' --loading' : ''}`}
      >
        {loading ? <Spin spinning={loading} /> : renderChart()}
      </ResponsiveContainer>
    </Card>
  );
}

export default ChartSection;
